// Compatibility adapter between the hosted AAA console and its Electron shell.
//
// Shell v1 advertises every native feature explicitly. The first prototype did
// not have a version/capabilities block, so function presence remains the N-1
// fallback. Browser tabs and partially-upgraded shells safely get no-op methods.

export type DesktopCapability = "notifications" | "taskbarBadge" | "deepLinks"

type NotificationInput = { title: string; body: string; url?: string }

type RawDesktopBridge = {
  apiVersion?: unknown
  platform?: unknown
  appVersion?: unknown
  channel?: unknown
  capabilities?: unknown
  notify?: unknown
  setBadge?: unknown
  onNavigate?: unknown
  ready?: unknown
}

type CapabilityMap = Record<DesktopCapability, boolean>

const NO_CAPABILITIES: CapabilityMap = {
  notifications: false,
  taskbarBadge: false,
  deepLinks: false,
}

function bridge(): RawDesktopBridge | null {
  if (typeof window === "undefined") return null
  const candidate = (window as unknown as { desktop?: unknown }).desktop
  return candidate && typeof candidate === "object" ? (candidate as RawDesktopBridge) : null
}

function advertisedCapabilities(raw: RawDesktopBridge): Partial<CapabilityMap> | null {
  if (!raw.capabilities || typeof raw.capabilities !== "object") return null
  return raw.capabilities as Partial<CapabilityMap>
}

/** Capabilities exposed by either bridge v1 or the legacy function-only bridge. */
export function desktopCapabilities(): CapabilityMap {
  const raw = bridge()
  if (!raw) return NO_CAPABILITIES

  const legacy = raw.apiVersion == null
  const versioned = typeof raw.apiVersion === "number" && raw.apiVersion >= 1
  const advertised = versioned ? advertisedCapabilities(raw) : null
  return {
    notifications:
      typeof raw.notify === "function" && (legacy || advertised?.notifications === true),
    taskbarBadge:
      typeof raw.setBadge === "function" && (legacy || advertised?.taskbarBadge === true),
    deepLinks:
      typeof raw.onNavigate === "function" && (legacy || advertised?.deepLinks === true),
  }
}

/** True when the console is running inside any recognized desktop shell. */
export function isDesktop(): boolean {
  return bridge() != null
}

export function hasDesktopCapability(capability: DesktopCapability): boolean {
  return desktopCapabilities()[capability]
}

/** Raise a native system notification when the installed shell supports it. */
export function notifyDesktop(input: NotificationInput): void {
  const raw = bridge()
  if (!raw || !hasDesktopCapability("notifications") || typeof raw.notify !== "function") return

  // Notification destinations cross a trust boundary again when clicked. Only
  // forward a route that this renderer would itself accept from the shell.
  const url = input.url ? validateDesktopRoute(input.url) ?? undefined : undefined
  const notify = raw.notify as (input: NotificationInput) => void
  notify({ ...input, url })
}

/** Set the dock/taskbar unread badge. Pass 0 or null to clear it. */
export function setDesktopBadge(count: number | null): void {
  const raw = bridge()
  if (!raw || !hasDesktopCapability("taskbarBadge") || typeof raw.setBadge !== "function") return
  const setBadge = raw.setBadge as (count: number | null) => void
  setBadge(count && count > 0 ? Math.floor(count) : null)
}

/** Subscribe to native notification/protocol navigation. */
export function onDesktopNavigate(handler: (url: string) => void): () => void {
  const raw = bridge()
  if (!raw || !hasDesktopCapability("deepLinks") || typeof raw.onNavigate !== "function") {
    return () => undefined
  }
  const unsubscribe = (raw.onNavigate as (handler: (url: string) => void) => unknown)(handler)
  return typeof unsubscribe === "function" ? (unsubscribe as () => void) : () => undefined
}

/**
 * Signal that the global navigation subscriber is installed. Bridge v1 uses
 * this to flush a cold-start deep link; on the legacy bridge it is a no-op.
 */
export function desktopRendererReady(): void {
  const raw = bridge()
  if (typeof raw?.apiVersion === "number" && raw.apiVersion >= 1 && typeof raw.ready === "function") {
    const ready = raw.ready as () => void
    ready()
  }
}

const SAFE_SEGMENT = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/
const RESERVED_ROOTS = new Set([
  "accept-invitation",
  "account",
  "admin",
  "api",
  "auth",
  "broadcast",
  "health",
  "invite",
  "oauth",
  "platform",
  "realtime",
  "set-password",
  "settings",
  "sign-in",
])
const EVENT_ROOTS = new Set([
  "accommodations",
  "activity",
  "badge-design",
  "badges",
  "channels",
  "conversations",
  "dietary",
  "documents",
  "emergency-contacts",
  "forms",
  "links",
  "locations",
  "lodging",
  "orders",
  "people",
  "profile",
  "reports",
  "schedule",
  "settings",
  "tickets",
  "travel",
  "vendors",
  "you",
])
const EVENT_ALIAS_ROOTS = new Set(["c", "d", "doc", "f", "m", "p", "person", "session", "stay", "t"])
const SETTINGS_PAGES = new Set([
  "attendee-data",
  "branding",
  "details",
  "feature-flags",
  "integrations",
  "invites",
  "permissions",
  "registration",
  "site",
  "ticketing",
])
const DETAIL_ROOTS = new Set(["locations", "lodging", "people", "schedule", "tickets", "travel", "vendors"])

/**
 * Validate an untrusted route received over IPC before giving it to TanStack.
 * Desktop links are path-only; auth redirects, queries, fragments, encoded or
 * traversal-like segments, and unknown console shapes are rejected.
 */
export function validateDesktopRoute(input: unknown): string | null {
  if (typeof input !== "string" || input.length < 2 || input.length > 2048) return null
  if (!input.startsWith("/") || input.startsWith("//") || /[\\?#%\u0000-\u001f]/.test(input)) return null
  if (input.endsWith("/") && input !== "/") return null

  const segments = input.slice(1).split("/")
  if (segments.some((segment) => !SAFE_SEGMENT.test(segment))) return null
  if (RESERVED_ROOTS.has(segments[0]!)) return null

  // Organization landing/settings pages.
  if (segments.length === 1) return input
  if (segments.length === 2 && (segments[1] === "people" || segments[1] === "settings")) return input
  if (segments.length < 2) return null

  // Event landing page.
  if (segments.length === 2) return input

  const tail = segments.slice(2)
  const root = tail[0]
  if (!root || (!EVENT_ROOTS.has(root) && !EVENT_ALIAS_ROOTS.has(root))) return null

  if (tail.length === 1) return EVENT_ROOTS.has(root) ? input : null
  if (EVENT_ALIAS_ROOTS.has(root)) return tail.length === 2 ? input : null

  if (root === "settings") {
    return tail.length === 2 && SETTINGS_PAGES.has(tail[1]!) ? input : null
  }
  if (root === "channels") {
    if (tail.length === 2) return input
    if (tail[1] === "guest" || tail[1] === "internal") {
      if (tail.length === 2 || tail.length === 3) return input
      if (tail.length === 4 && tail[3] === "settings") return input
      if (tail.length === 5 && tail[3] === "thread") return input
    }
    return null
  }
  if (root === "conversations") {
    return tail.length === 2 || (tail.length === 4 && tail[2] === "thread") ? input : null
  }
  if (root === "forms") return tail.length === 2 || tail.length === 3 ? input : null

  return tail.length === 2 && DETAIL_ROOTS.has(root) ? input : null
}
